import { useMemo } from "react";
import { Button, Flex, Skeleton } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { ELocalStorage } from "../../../assets/storage/localStorage";
import { fetchFromLocalStorage } from "../../../assets/storage/localStorage";
import { TSimulatorLiveModeReviewTicket } from "../../simulator-live-mode/types";
import styles from "./styles.module.scss";
import cx from "classnames";
import { TrainingCenterIcon } from "@/v2/assets/mor-shapes/TrainingCenterIcon";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { useWorkspace } from "../context/wrokspace";
import { useExploreInteractions } from "@/v2/entities/explore/Explore.api";
import { Filter } from "@/v2/entities/explore/Explore.filter";

export const MoveToLiveModeCTA = ({
  topic,
  topics = [],
  channel,
  tenantGroup,
  customField,
}: {
  topic?: string | null;
  topics: Topic[] | null;
  channel?: string;
  tenantGroup?: string[];
  customField?: string[];
}) => {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const { simulatorId } = useParams();

  const topicName = useMemo(() => {
    const topicObj = topics?.find((t) => t.id.toString() === topic);
    return topicObj?.name || topicObj?.topic;
  }, [topics, topic]);

  const filter = useMemo<Filter | undefined>(() => {
    if (topicName == null) {
      return;
    }

    const last30Days = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);

    const filter: Filter = {
      and: [
        { createdAt: { gte: last30Days.toISOString() } },
        { topic: { eq: topicName } },
        ...(workspace === "notable" ? [{ partner: { in: ["Compass"] } }] : []),
      ],
    };

    return filter;
  }, [topicName, workspace]);

  const { data, isLoading } = useExploreInteractions({
    source: "tickets",
    sorting: [{ id: "createdAt", desc: true }],
    filter,
    enabled: !!topicName && !!filter,
  });

  const openTicketsCount = data?.length;
  const openTicketsIds = data?.map((ticket) => ticket.id) ?? [];

  const reviewedCount = useMemo(() => {
    const tickets = fetchFromLocalStorage(
      `${ELocalStorage.LIVE_TEST_MODE}-${topic}`
    );
    return tickets && Array.isArray(openTicketsIds)
      ? JSON.parse(tickets).filter(
          (ticket: TSimulatorLiveModeReviewTicket) =>
            openTicketsIds?.includes(ticket.ticketId) &&
            ticket.good !== undefined &&
            ticket.good !== null
        ).length
      : 0;
  }, [topic, openTicketsIds]);

  if (!topicName) {
    return null;
  }

  const handleStartTest = () => {
    let url = `/automation/${simulatorId}/${topic}/live-test`;
    const queryParams = [];

    if (channel) {
      queryParams.push(`channel=${channel}`);
    }
    if (tenantGroup && tenantGroup.length > 0 && tenantGroup[0]) {
      queryParams.push(`tenantGroup=${tenantGroup}`);
    }
    if (customField && customField.length > 0 && customField[0]) {
      queryParams.push(`customField=${customField}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }
    navigate(url);
  };

  const hasOpenTickets = openTicketsCount && openTicketsCount > 0;
  const hasUnreviewedTickets =
    openTicketsCount && openTicketsCount > reviewedCount;

  return (
    <div
      className={cx(styles.liveModeCTAContainer, {
        [styles.empty]: !hasOpenTickets,
      })}
    >
      <div className={styles.leftContent}>
        <TrainingCenterIcon isActive={!isLoading} />
        <Flex direction="column" gap="1">
          <Skeleton loading={isLoading} width="100%" height="100%">
            <h4>
              {hasOpenTickets
                ? `${openTicketsCount} open tickets available for testing`
                : "No tickets available for testing"}
            </h4>
          </Skeleton>
          <Skeleton loading={isLoading} width="100%" height="100%">
            {hasOpenTickets ? (
              <p className={styles.reviewedText}>{reviewedCount} reviewed</p>
            ) : null}
          </Skeleton>
        </Flex>
      </div>
      {hasOpenTickets ? (
        <div className={styles.rightContent}>
          <Button
            variant="outline"
            color="gray"
            highContrast
            disabled={!hasUnreviewedTickets}
            onClick={handleStartTest}
          >
            Start <ArrowRightIcon />
          </Button>
        </div>
      ) : null}
    </div>
  );
};
